/* eslint-disable react/no-unescaped-entities */
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion, useSpring, useTransform } from 'framer-motion';
import { CTA, IImage } from '../types';
import { MotionImage } from './AnimatedImage';
import { Button } from '.';
import { makeVariants } from '../utils/makeVariants';
import { useScrolledTheme, useInViewScroll, SplitH2, useSanitizedTranslation } from 'ui';
import Head from 'next/head';
import { BazaarVoiceBlock } from './BazaarVoiceBlock';
import Image from 'next/image';
import devicesJSON from 'ui/devices.json';

const DeviceOpenGraph = ({ heading, description, images, url }) => {
  const getGalleryJsonLd = () => {
    const obj = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      '@id': 'https://www.xumo.com' + url,
      brand: 'Xumo',
      name: heading,
      description: description,
      image: images.src,
    };

    return {
      __html: JSON.stringify(obj),
    };
  };

  return (
    <>
      <Head>
        <script type="application/ld+json" dangerouslySetInnerHTML={getGalleryJsonLd()} />
      </Head>
    </>
  );
};

export type ProductDetailBlockProps = {
  deviceId: string;
  size: number;
};

// TODO: add more props
//const ShopButton: React.FC<{ image: IImage }> = ({ image }) => {
const ShopButton = ({ image, url, name, label, bvValue }) => {
  return (
    <div className="inline-block">
      <Button
        label={label}
        href={url}
        disabled={!url}
        buttonType={!url ? 'submit-primary' : 'link-primary'}
        onClick={evt => {
          evt.preventDefault();

          BV &&
            BV.pixel.trackConversion({
              type: 'Shop now',
              label: name,
              value: bvValue,
            });

          window.open(evt.currentTarget.href, '_blank');
        }}
        styleOverrides={{
          tailwind:
            'inline-block px-6 bg-xumoBerry disabled:bg-xumoSmoke cursor-pointer disabled:cursor-default disabled:text-xumoGrey disabled:drop-shadow-none',
          css: {
            backgroundColor: '',
            color: 'white',
          },
        }}
      />
    </div>
  );
};

export const ProductDetailBlock: React.FC<ProductDetailBlockProps> = ({ deviceId = 0, size }) => {
  const { t } = useSanitizedTranslation();

  const deviceIndex = typeof deviceId === 'string' ? parseInt(deviceId) : deviceId;
  const { productId = '001', features, buttons, sizes, heading: h1, brand, logo, images, redirectUrl, customUrls, worksWith } = devicesJSON[deviceIndex];

  const heading = h1.replace('$size', size + '"');

  const newButton = Array.isArray(buttons) && buttons.length > 0 ? [...buttons] : [];
  if (newButton.length > 0) {
    // Check if there are custom URLs for this device and size
    if (customUrls && customUrls[size.toString() as keyof typeof customUrls]) {
      newButton[0] = {
        ...newButton[0],
        url: customUrls[size.toString() as keyof typeof customUrls],
      };
    } else {
      // Use the default $size replacement
      newButton[0] = {
        ...newButton[0],
        url: newButton[0].url.replace('$size', size.toString()),
      };
    }
  }

  return (
    <>
      <DeviceOpenGraph heading={heading} description={features.join(', ')} images={images} url={redirectUrl} />
      <motion.section className={'wrapper'} data-test-id="product-detail-block" style={{ color: '#000' }}>
        <div className="content-wrapper">
          <div className="text-left">
            <div className="flex flex-col-reverse md:flex-row">
              <div className="relative w-full rounded-xl bg-white p-4 md:w-3/5">
                <div className="relative aspect-video">
                  <Image fill className="object-contain" {...images} />
                </div>
                <div className="align-center relative mt-4 flex h-[40px] justify-center space-x-4">
                  {worksWith?.appleAirplay && (
                    <div className="flex h-[40px] w-[148px] items-center justify-center">
                      <Image
                        alt="Works with Apple AirPlay"
                        src={'/static/images/works-with-airplay.svg'}
                        height={40}
                        width={148}
                        sizes="(max-width: 768px) 100vw, (max-width: 1920px) 50vw, 25vw"
                        className="max-h-[40px] object-contain"
                      />
                    </div>
                  )}
                  {worksWith?.appleHome && (
                    <div className="flex h-[40px] w-[148px] items-center justify-center">
                      <Image
                        alt="Works with Apple Home"
                        src={'/static/images/works-with-applehome.svg'}
                        height={40}
                        width={148}
                        sizes="(max-width: 768px) 100vw, (max-width: 1920px) 50vw, 25vw"
                        className="max-h-[40px] object-contain"
                      />
                    </div>
                  )}
                  {worksWith?.alexa && (
                    <div className="flex h-[40px] w-[102px] items-center justify-center">
                      <Image
                        alt="Works with Alexa"
                        src={'/static/images/works-with-alexa.svg'}
                        height={40}
                        width={102}
                        sizes="(max-width: 768px) 100vw, (max-width: 1920px) 50vw, 25vw"
                        className="max-h-[40px] object-contain"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="mb-4 space-y-4 md:pl-8">
                <div className="relative w-full" style={{ height: 30, marginBottom: 10 }}>
                  <Image
                    alt={brand + ' logo'}
                    src={logo}
                    sizes="(max-width: 768px) 100vw, (max-width: 1920px) 50vw, 25vw"
                    fill
                    className="object-contain object-left"
                  />
                </div>
                <h1 className="type-subheading-small">{t(heading)}</h1>
                {!!productId && <div data-i18n data-bv-show="rating_summary" data-bv-product-id={productId} />}
                <ul className="list-disc pl-4">
                  {features.map(feature => (
                    <li key={feature} className="type-body-base" data-i18n>
                      {t(feature)}
                    </li>
                  ))}
                </ul>
                {newButton.length > 0 && <ShopButton {...newButton[0]} />}
              </div>
            </div>
          </div>
        </div>
      </motion.section>
    </>
  );
};
